import { IConfig } from '../../config';
import { ConnectionState } from '../../types';
import { NbLogger } from '../../utils';
import { SSEClient } from './sseClient';

export abstract class BaseSSEConnection<T> {
  protected reconnectLaterTimeout?: number;
  protected reconnectCounter = 0;
  protected connectionState: ConnectionState = ConnectionState.Disconnected;
  protected abstract source: string;

  constructor(
    protected logger: NbLogger,
    protected client: SSEClient<T>,
    protected offlineMode: boolean,
    protected onConnectionStateChange: (connectionState: ConnectionState, broadcastConnectionState: boolean) => void,
    protected config: IConfig,
  ) {
    client.setEventHandlers({
      onMessageCallback: this.onMessageReceived.bind(this),
      onConnectError: this.onConnectError.bind(this),
      onConnectSuccess: this.onConnectSuccess.bind(this),
    });
  }

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Sets the connection state
   * @param state new connection state
   * @param broadcastConnectionState if set to true, broadcasts the connection state
   */
  protected updateConnectionState(state: ConnectionState, broadcastConnectionState: boolean) {
    this.connectionState = state;

    this.onConnectionStateChange(this.connectionState, broadcastConnectionState);
  }

  /* eslint-disable @typescript-eslint/no-unused-vars */
  protected abstract onMessageReceived(message: T): void;

  /**
   * On SSE client connect error, it updates the connection state and tries to reconnect later
   */
  protected onConnectError(silentConnectRequested: boolean) {
    this.updateConnectionState(ConnectionState.FailedToConnected, silentConnectRequested);
    this.startReconnectLater();
  }

  /**
   * Handles if the SSE client connects successfully
   * @param silentConnectRequested true when sse connection is requested silently
   */
  protected onConnectSuccess(silentConnectRequested: boolean) {
    this.updateConnectionState(ConnectionState.Connected, !silentConnectRequested);
    this.reconnectCounter = 0;
    this.sseConnected();
  }

  /**
   * Connects to the SSE server using the sseClient.
   * @param isSilent if set to true, there will be no error message displayed when connection fails
   */
  public connectSSEClient({ isSilent = false }: { isSilent?: boolean }): void {
    if (this.offlineMode) {
      this.logger.trace(`${this.source} SSE connection is in offline mode`);
      return;
    }
    if (this.connectionState === ConnectionState.Connecting || this.connectionState === ConnectionState.Connected) {
      this.logger.trace(`${this.source} connection is already in progress or done (currently ${this.connectionState})`);
      return;
    }

    this.updateConnectionState(ConnectionState.Connecting, isSilent);

    this.client.connect(isSilent);
  }

  /**
   * Handles if the eStream disconnects. If there was a connection before, it tries to reconnect later
   */
  protected onSSEClientDisconnect() {
    if (this.connectionState === ConnectionState.Connected && !this.reconnectLaterTimeout) {
      this.startReconnectLater();
    }
    this.updateConnectionState(ConnectionState.FailedToConnected, false);
    this.client.disconnect();
  }

  /**
   * Creates a timeout to reconnect later
   * @private
   */
  protected startReconnectLater() {
    this.reconnectLaterTimeout = setTimeout(
      this.reConnectLater.bind(this),
      this.reconnectCounter > 3 ? this.config.SSE.GeneralReconnectTimeout : this.config.SSE.FastReconnectTimeout,
    ) as unknown as number;
  }

  /**
   * If we haven't given up on EStream, tries to connect
   * @private
   */
  protected async reConnectLater() {
    this.reconnectCounter += 1;
    this.logger.debug(`${this.source} reconnecting (${this.reconnectCounter})...`);
    if (this.reconnectLaterTimeout) {
      clearTimeout(this.reconnectLaterTimeout);
      this.reconnectLaterTimeout = undefined;
    }

    this.connectSSEClient({ isSilent: true });
  }

  protected async sseConnected() {}

  setAsPrimaryInstance() {
    this.offlineMode = false;
  }

  /**
   * Disconnects the EStream client, and updates the connection state
   * @param force if set to true, there won't be any connection state update
   */
  disconnectSSEClient(force?: boolean) {
    if (this.reconnectLaterTimeout) {
      clearTimeout(this.reconnectLaterTimeout);
      this.reconnectLaterTimeout = undefined;
      this.reconnectCounter = 0;
      this.connectionState = ConnectionState.Disconnected;
    }

    if (!force) {
      this.updateConnectionState(ConnectionState.Disconnected, false);
    } else if (this.connectionState !== ConnectionState.Disconnected) {
      this.updateConnectionState(ConnectionState.Disconnected, false);
    }
    this.client.disconnect();
  }
}
